import { useMutation } from '@tanstack/react-query';
import type { AxiosError, AxiosInstance } from 'axios';
import { jotaiStore } from 'init/jotai';
import { i18nKeys, translationStateAtom } from 'locales';
import {
  filter,
  flat,
  isArray,
  isString,
  keys,
  map,
  mapKeys,
  partition,
  pipe,
  toSnakeCase,
} from 'remeda';
import { z } from 'zod/v4';

import { type ApiResponse, addActionNameToMutationResult, useLegacyAxiosInstance } from './utils';

const { t } = jotaiStore.get(translationStateAtom);

const REQUIRED_ADDRESS_FIELDS = ['streetName', 'zipCode', 'countryCode'] as const;

const isFilled = (value: unknown) => isString(value) && value.trim() !== '';

export const AddressSchema = z
  .object({
    countryCode: z.string().nullable(),
    city: z.string().optional(),
    zipCode: z.string().optional(),
    streetName: z.string().optional(),
    streetNumber: z.string().optional(),
    streetBox: z.string().optional(),
    additionalLine1: z.string().optional(),
    additionalLine2: z.string().optional(),
  })
  .superRefine((address, ctx) => {
    if (!Object.values(address).some(isFilled)) return;

    REQUIRED_ADDRESS_FIELDS.filter(field => !isFilled(address[field])).forEach(field => {
      ctx.addIssue({
        code: 'custom',
        path: [field],
        message: t(i18nKeys.ERROR.FIELD_REQUIRED),
      });
    });
  });

export const ClientFormSchema = z.object({
  fullName: z.string().min(2, { error: t(i18nKeys.ERROR.FIELD_REQUIRED) }),
  moralEntity: z.boolean(),
  locale: z.string().nullable(),
  vatNumber: z.string().optional(),
  reference: z.string().optional(),
  currency: z.string().nullable(),
  coContractor: z.boolean(),
  intracom: z.boolean(),
  addressAttributes: AddressSchema,
  emailsAttributes: z.array(z.string()),
  emails: z
    .array(
      z.object({
        email: z.string().refine(val => val === '' || z.email().safeParse(val).success, {
          error: t(i18nKeys.ERROR.INVALID_EMAIL),
        }),
        isDefault: z.boolean(),
      }),
    )
    .optional(),
  phones: z
    .array(
      z.object({
        id: z.string().optional(),
        number: z.string(),
        _destroy: z.boolean().optional(),
      }),
    )
    .optional(),
  bankAccounts: z
    .array(
      z.object({
        id: z.string().optional(),
        iban: z.string().refine(val => val.length >= 14 || val.length === 0, {
          error: t(i18nKeys.ERROR.INVALID_IBAN),
        }),
        bic: z.string().refine(val => val.length >= 8 || val.length === 0, {
          error: t(i18nKeys.ERROR.INVALID_BIC),
        }),
        _destroy: z.boolean().optional(),
      }),
    )
    .refine(
      data => {
        // If BIC is provided but IBAN is empty, return false
        if (data.some(account => account.bic?.trim() && !account.iban)) {
          return false;
        }
        return true;
      },
      {
        error: t(i18nKeys.ERROR.INVALID_IBAN_BIC),
        path: ['iban'],
      },
    )
    .optional(),
});

export const ClientFormSchemaSnakeCase = ClientFormSchema.transform(data => {
  const transformedData: Record<string, unknown> = {};

  transformedData.phones_attributes = data.phones;
  transformedData.debtor_bank_accounts_attributes = data.bankAccounts;

  keys(data).forEach(key => {
    transformedData[toSnakeCase(key)] = data[key];
  });

  transformedData.address_attributes = pipe(
    data.addressAttributes,
    mapKeys(key => toSnakeCase(key)),
  );

  if (data.emails && isArray(data.emails)) {
    transformedData.emails = pipe(
      data.emails,
      filter(email => Boolean(email.email?.trim())),
      partition(email => email.isDefault),
      flat(),
      map(email => email.email),
    );
  }

  return transformedData;
});

export type CreateClientForm = z.infer<typeof ClientFormSchema>;

export async function createClientFn(
  axiosInstance: Promise<AxiosInstance>,
  { data }: { data: CreateClientForm },
) {
  const instance = await axiosInstance;
  const snakeCaseData = ClientFormSchemaSnakeCase.parse(data);
  return instance.post(`/debtors`, snakeCaseData);
}

export function useCreateClient() {
  const axiosInstance = useLegacyAxiosInstance();

  const mutation = useMutation<
    ApiResponse,
    AxiosError<{ error_message: Array<string> }>,
    { data: CreateClientForm }
  >({
    mutationFn: variables => createClientFn(axiosInstance, variables),
  });

  return addActionNameToMutationResult<
    ApiResponse,
    AxiosError<{ error_message: Array<string> }>,
    { data: CreateClientForm },
    'createClient'
  >('createClient', mutation);
}
